/**
 * Experiments list states. Presentational: SplitTesting passes real data, the
 * DEV states gallery passes Paper's mocks.
 *
 * Paper → Experiments: "Experiments · List · Loading" (real labels, grey
 * bars where numbers go) and "Experiments · No tests yet" (the table frame
 * stays, the empty body points at the busiest page and the planner).
 */
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

export function ExperimentsHeader({
  title = "Experiments",
  subtitle,
  actions,
}: {
  title?: string
  subtitle: string
  actions?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">
          {title}
        </h1>
        <p className="text-[13px] leading-4 text-muted-foreground">{subtitle}</p>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

/** Same lanes as ExperimentDecisionTable. */
const COL = {
  next: "w-[112px] shrink-0",
  status: "w-[124px] shrink-0",
  test: "min-w-0 flex-1 basis-0",
  goal: "w-[140px] shrink-0",
  result: "w-[230px] shrink-0",
  visitors: "w-[72px] shrink-0 text-right",
}

const ROW = "flex items-center gap-4 px-5"

export function ExperimentsTableHeader() {
  return (
    <div role="row" className={cn(ROW, "h-9 border-b border-border bg-muted/50 text-xs text-muted-foreground")}>
      <div role="columnheader" className={COL.next}>Next step</div>
      <div role="columnheader" className={COL.status}>Status</div>
      <div role="columnheader" className={COL.test}>Test</div>
      <div role="columnheader" className={COL.goal}>Goal</div>
      <div role="columnheader" className={COL.result}>Result</div>
      <div role="columnheader" className={COL.visitors}>Visitors</div>
    </div>
  )
}

/** Bar widths per row, from the Paper artboard. */
const LOADING_ROWS: Array<{
  next: number | "text"
  status: number
  test: [number, number]
  goal: number
  result: { chip?: number; text: number; progress?: boolean }
  visitors: number
}> = [
  { next: 62, status: 94, test: [212, 250], goal: 80, result: { chip: 83, text: 134 }, visitors: 16 },
  { next: "text", status: 47, test: [230, 180], goal: 58, result: { text: 114, progress: true }, visitors: 36 },
  { next: 88, status: 47, test: [218, 170], goal: 95, result: { text: 215 }, visitors: 16 },
  { next: 88, status: 47, test: [270, 236], goal: 58, result: { text: 141 }, visitors: 16 },
  { next: 56, status: 27, test: [288, 258], goal: 51, result: { text: 101 }, visitors: 16 },
]

function Bar({ h, w, className }: { h: number; w: number; className?: string }) {
  return <Skeleton className={cn("shrink-0", className)} style={{ height: h, width: w }} />
}

export function ExperimentsListLoading() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true" aria-label="Loading experiments">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground">Experiments</h1>
        <div className="flex h-4 items-center">
          <Bar h={13} w={371} className="max-w-full" />
        </div>
      </div>

      <div className="well flex flex-col gap-2">
        <div className="surface-raised flex flex-wrap items-center gap-x-6 gap-y-3 px-5 py-4 sm:flex-nowrap sm:gap-x-0">
          <div className="flex items-end gap-2 sm:pr-7">
            <div className="flex h-7 items-center"><Bar h={22} w={24} /></div>
            <span className="text-[13px] leading-4 text-muted-foreground">running test</span>
          </div>
          <span aria-hidden className="hidden h-7 w-px shrink-0 bg-foreground/[0.08] sm:block" />
          <div className="flex min-w-0 items-end gap-2 sm:px-7">
            <div className="flex h-7 items-center"><Bar h={22} w={49} /></div>
            <div className="flex h-4 min-w-0 items-center"><Bar h={13} w={360} className="max-w-[50vw]" /></div>
          </div>
          <span aria-hidden className="hidden h-7 w-px shrink-0 bg-foreground/[0.08] sm:block" />
          <div className="flex items-end gap-2 sm:pl-7">
            <div className="flex h-7 items-center"><Bar h={22} w={62} /></div>
            <span className="text-[13px] leading-4 text-muted-foreground">visitors in tests so far</span>
          </div>
        </div>

        <div className="surface-raised overflow-clip">
          <div className="overflow-x-auto">
            <div className="min-w-[880px]" role="table" aria-label="Experiments">
              <ExperimentsTableHeader />
              {LOADING_ROWS.map((r, i) => (
                <div key={i} role="row" className={cn(ROW, "h-14", i > 0 && "border-t border-border/60")}>
                  <div className={cn(COL.next, "flex")}>
                    {r.next === "text" ? <Bar h={12} w={121} /> : <Skeleton className="h-7 rounded-lg" style={{ width: r.next }} />}
                  </div>
                  <div className={cn(COL.status, "flex items-center pl-3")}>
                    <Bar h={12} w={r.status} />
                  </div>
                  <div className={cn(COL.test, "flex flex-col gap-0.5")}>
                    <div className="flex h-[18px] items-center"><Bar h={14} w={r.test[0]} className="max-w-full" /></div>
                    <div className="flex h-4 items-center"><Bar h={12} w={r.test[1]} className="max-w-full" /></div>
                  </div>
                  <div className={cn(COL.goal, "flex h-4 items-center")}>
                    <Bar h={13} w={r.goal} />
                  </div>
                  <div className={COL.result}>
                    {r.result.progress ? (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex h-4 items-center"><Bar h={12} w={r.result.text} /></div>
                        <div className="h-1 w-[180px] rounded-sm bg-muted" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        {r.result.chip && <Skeleton className="h-[22px] shrink-0 rounded-md" style={{ width: r.result.chip }} />}
                        <Bar h={12} w={r.result.text} />
                      </div>
                    )}
                  </div>
                  <div className={cn(COL.visitors, "flex h-4 items-center justify-end")}>
                    <Bar h={13} w={r.visitors} />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex h-11 items-center justify-between border-t border-border bg-muted/50 px-5 text-xs font-medium">
            <Bar h={12} w={128} />
            <span className="text-foreground">+ Capture idea</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export type BusiestPage = {
  /** "acme.com/" */
  label: string
  /** "Your busiest page · 4,510 visitors this week · 62% of visitors" */
  detail: string
}

/**
 * Empty body for the experiments table: Paper "Experiments · No tests yet".
 * The busiest-page card only renders when page traffic is known.
 */
export function ExperimentsNoTests({
  busiestPage,
  onTestPage,
  onOpenPlanner,
}: {
  busiestPage?: BusiestPage | null
  onTestPage?: () => void
  onOpenPlanner?: () => void
}) {
  return (
    <div className="flex flex-col items-center px-4 pb-[52px] pt-14">
      <div className="mb-4 flex size-10 items-center justify-center rounded-lg bg-muted/50 ring-1 ring-border">
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden className="text-foreground/35">
          <path
            d="M9 3h6M10 3v6l-5.5 10a1.5 1.5 0 0 0 1.3 2h12.4a1.5 1.5 0 0 0 1.3-2L14 9V3"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h2 className="mb-1.5 text-center text-sm font-medium leading-[18px] text-foreground">No tests yet</h2>
      <p className="max-w-[440px] text-center text-[13px] leading-5 text-muted-foreground">
        A test shows which version of a page gets more sign-ups, so changes ship on evidence instead of opinion.
      </p>

      {busiestPage && (
        <div className="mt-6 flex w-full max-w-[520px] flex-col overflow-clip rounded-xl bg-card ring-1 ring-border">
          <div className="border-b border-foreground/[0.045] px-3.5 py-2.5 text-xs leading-4">
            <span className="text-xs font-medium leading-4 text-foreground/40">Start where the traffic is</span>
          </div>
          <div className="flex items-center gap-3 px-3.5 py-3">
            <div className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5">
              <span className="truncate text-sm font-medium leading-[18px] text-foreground">{busiestPage.label}</span>
              <span className="text-xs leading-4 text-muted-foreground">{busiestPage.detail}</span>
            </div>
            {onTestPage && (
              <Button variant="outline" size="sm" className="h-8 px-3 text-[13px]" onClick={onTestPage}>
                Test this page
              </Button>
            )}
          </div>
        </div>
      )}

      {onOpenPlanner && (
        <p className="mt-4 flex flex-wrap items-center justify-center gap-1.5 text-[13px] leading-4">
          <span className="text-muted-foreground">Not sure how long a test needs?</span>
          <button
            type="button"
            onClick={onOpenPlanner}
            className="font-medium text-foreground underline decoration-foreground/20 decoration-1 underline-offset-[3px] hover:decoration-foreground/60"
          >
            Open the test planner
          </button>
        </p>
      )}
    </div>
  )
}
